import { scoreToGrade } from '@rg/shared';
import { useCountUp, useInView } from '@/hooks/use-motion';
import { cn, scoreTone } from '@/lib/utils';

interface Props {
  score: number;
  size?: number;
  label?: string;
  className?: string;
  /** Count-up duration in ms. */
  durationMs?: number;
}

/** Large circular score visual. Accessible via role="img" + aria-label. */
export function ScoreGauge({
  score,
  size = 200,
  label = 'Website score',
  className,
  durationMs = 1400,
}: Props) {
  const clamped = Math.max(0, Math.min(100, Math.round(score)));
  const r = 54;
  const c = 2 * Math.PI * r;
  const grade = scoreToGrade(clamped);
  // Ring fills and the number counts up when the gauge scrolls into view.
  const { ref, visible } = useInView<HTMLDivElement>();
  const shown = useCountUp(clamped, { start: visible, durationMs });
  // Colour follows the counting number (e.g. amber → green as a score climbs).
  const tone = scoreTone(shown);
  return (
    <div
      ref={ref}
      className={cn('relative inline-grid place-items-center', className)}
      style={{ width: size, height: size }}
    >
      <svg
        viewBox="0 0 120 120"
        className="-rotate-90"
        width={size}
        height={size}
        role="img"
        aria-label={`${label}: ${clamped} out of 100, ${grade}`}
      >
        <circle cx="60" cy="60" r={r} fill="none" strokeWidth="10" className="stroke-ink-100" />
        <circle
          cx="60"
          cy="60"
          r={r}
          fill="none"
          strokeWidth="10"
          strokeLinecap="round"
          className={cn(tone.ring, 'drop-shadow-sm transition-[stroke] duration-500')}
          strokeDasharray={c}
          strokeDashoffset={c * (1 - shown / 100)}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center" aria-hidden>
        <span
          className="font-display text-5xl font-bold leading-none text-ink-900"
          style={{ fontSize: size / 4 }}
        >
          {shown}
        </span>
        <span className="mt-1 text-sm text-ink-500">/ 100</span>
        <span
          className={cn(
            'mt-1 text-sm font-bold transition-opacity duration-500',
            tone.text,
            shown === clamped ? 'opacity-100' : 'opacity-0',
          )}
        >
          {grade}
        </span>
      </div>
    </div>
  );
}
